<template>
  <div class="position-relative" style="min-height: 500px">
    <le-loading v-if="isLoading"></le-loading>
    <div v-if="!isLoading && (!model.games || Object.keys(model.games).length === 0)" class="text-center p-5 text-muted">
      <span v-if="currentTimeline === 'published'">{{ $t('noPublishedGames') }}</span>
      <span v-else-if="currentTimeline === 'unpublished'">{{ $t('noUnpublishedGames') }}</span>
      <span v-else>{{ $t('noGames') }}</span>
    </div>
    <div v-for="(date, n) in model.games" :key="`date-${n}`">
      <ntm-block>
        <h2>{{ $gameTime(n, 'dateLong') }}</h2>
        <div v-for="(venue, n) in date" :key="`venue-${n}`" class="venue-caption">
          <h3>{{ n }}</h3>
          <table class="table table-bordered table-striped table-vcenter">
            <thead>
            <tr>
              <th>Code</th>
              <th>Time</th>
              <th>League</th>
              <th class="text-center">Game</th>
              <th class="text-center">Actions</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="game in venue" :key="game.uuid" :class="gameStatusColor(game.status)">
              <td>
                <template v-if="['SCHEDULED', 'FINISHED'].includes(game.status)">
                  <div class="btn-group">
                    <button class="btn btn-info btn-sm"
                            v-if="['SCHEDULED', 'FINISHED'].includes(game.status) && game.teamA && game.teamB"
                            @click="scheduleGameModal(game)"
                            v-tooltip="$t('tooltip.rescheduleGame')"><i class="fa fa-clock-o"></i></button>
                    <button class="btn btn-warning btn-sm"
                            v-if="['SCHEDULED'].includes(game.status) && game.teamA && game.teamB"
                            @click="cancelScheduledGame(game)"
                            v-tooltip="$t('tooltip.removeFromSchedule')"><i class="fa fa-times"></i></button>
                  </div>
                </template>
                <le-regenerate-game-code v-if="['ARCHIVED'].includes(game.status)" :game-uuid="game.uuid" @codeGenerated="(value) => codeGenerated(game, value)"></le-regenerate-game-code>
                <span class="ml-2 text-nowrap" :class="{'text-warning': newCode === game.code}" v-if="game.code">{{
                    formattedGameCode(game.code)
                  }}</span>
              </td>
              <td>{{ $gameTime(game.time, 'time') }}</td>
              <td>{{ game.league.name }}</td>
              <td class="text-center"><strong>{{ game.teamA?.name ?? '?' }}</strong> :
                <strong>{{ game.teamB?.name ?? '?' }}</strong></td>
              <td class="text-center actions-cell">
                <div>
                <le-copy-to-clipboard v-if="!['ARCHIVED'].includes(game.status)" icon="fa fa-calculator" class="m-1"
                                      :text="getScorekeeperGameUrl(game.code)" :tooltip="$t('tooltip.copyScorekeeperUrl')"></le-copy-to-clipboard>
                <le-generate-game-news :game="game"></le-generate-game-news>
                <le-copy-to-clipboard icon="fa fa-globe" class="m-1"
                                      :text="getGameWebsiteUrl(game)" :tooltip="$t('tooltip.copyWebsiteUrl')"></le-copy-to-clipboard>
                <game-embed-video :game="game" @changed="fetchData"></game-embed-video>
                <le-y-t-live class="m-1" :schedule="game.schedule" :disable-buttons="!['SCHEDULED', 'LIVE'].includes(game.status)" :game="game" @changed="fetchData"></le-y-t-live>
                <router-link
                  :to="{ name: 'schedules.games.showGallery', params: {uuid: game.schedule.uuid, game: game.uuid }}"
                  class="btn btn-sm btn-primary"
                  v-tooltip="$t('game.gallery')">
                  <i class="fa fa-image"></i>
                </router-link>
                <button v-if="['NOT_PLAYED', 'SCHEDULED'].includes(game.status)" @click="swapGameTimeAndVenue(game)" :class="{'btn-success': swapGame?.uuid === game.uuid, 'btn-info': swapGame?.uuid !== game.uuid}" class="btn btn-sm m-1" v-tooltip="$t('game.swapTimeAndVenue')"><i class="fa fa-arrows-v"></i></button>
                </div>
              </td>
            </tr>
            </tbody>
          </table>
        </div>
      </ntm-block>
    </div>
    <le-schedule-game-modal ref="scheduleModal" @saved="onGameSaved"></le-schedule-game-modal>
  </div>
</template>

<script>
import GameEmbedVideo from '@/components/le/GameEmbedVideo.vue'
import LeYTLive from '@/components/le/LeYTLive.vue'
import { gameStatusColor } from '@/lib/status'
import GameService from '@/services/game.service'
import DashboardService from '@/services/dashboard.service'
import LeScheduleGameModal from '@/components/le/LeScheduleGameModal.vue'
import { useRouteQuery } from '@/use/useRouteQuery'
import { ref } from 'vue'
import { useGameSwap } from '@/use/useGameSwap'
import { useTimeline } from '@/use/useTimeline'
import { useExternalLinks } from '@/use/useExternalLinks'
import LeLoading from '@/components/ntm/LeLoading.vue'
import { formattedGameCode } from '@/lib/gameCode'
import LeRegenerateGameCode from '@/components/le/LeRegenerateGameCode.vue'

export default {
  components: { LeScheduleGameModal, LeRegenerateGameCode, LeLoading, LeYTLive, GameEmbedVideo },
  data () {
    return {
      view: 'calendar',
      newCode: null
    }
  },
  setup () {
    const currentTimeline = useRouteQuery('timeline', 'all')
    const model = ref({})
    const isLoading = ref(false)
    function setData (data) {
      model.value = data
    }
    function fetchData () {
      isLoading.value = true
      DashboardService.index(currentTimeline.value).then((res) => {
        setData(res.data)
      }).finally(() => {
        isLoading.value = false
      })
    }

    const { swapGame, swapGameTimeAndVenue } = useGameSwap(() => fetchData())

    const { timeline, fetchCurrentTimeline, publishTimeline } = useTimeline(() => {
      currentTimeline.value = 'all'

      setTimeout(() => {
        fetchData()
      })
    })
    const { getScorekeeperGameUrl, getGameWebsiteUrl, getGameBroadcastWebsiteUrl } = useExternalLinks()

    return {
      isLoading,
      model,
      swapGame,
      swapGameTimeAndVenue,
      setData,
      fetchData,
      getGameBroadcastWebsiteUrl,
      getScorekeeperGameUrl,
      getGameWebsiteUrl,
      timeline,
      fetchCurrentTimeline,
      publishTimeline,
      currentTimeline
    }
  },
  mounted () {
    this.fetchData()
  },
  watch: {
    currentTimeline: 'fetchData'
  },
  methods: {
    formattedGameCode (code) {
      return formattedGameCode(code)
    },
    gameStatusColor,
    scheduleGameModal (game) {
      this.$refs.scheduleModal.open(game)
    },
    onGameSaved () {
      this.fetchData()
    },
    cancelScheduledGame (game) {
      GameService.cancelScheduledGame(game.schedule.uuid, game.uuid).then(() => {
        this.fetchData()
        this.fetchCurrentTimeline()
      })
    },
    codeGenerated (game, data) {
      console.log('codeGenerated', game, data)
      game.code = data.code
      game.status = data.status
      this.newCode = data.code

      setTimeout(() => {
        this.newCode = null
      }, 10000)
    }
  }
}

</script>

<style scoped>
.actions-cell ::v-deep .btn {
  height: 32px;
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
</style>
